import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "animate.css";

import EmptyBox from "../components/EmptyBox";
import PageLayout from "../components/PageLayout";
import WishFlexBox from "../components/WishFlexBox";
import WishSimpleCard from "../components/WishSimpleCard";
import WishToaster from "../components/WishToaster";
import DocumentVerificationModal from "../components/DocumentVerificationModal";
import LoadingNote from "../components/LoadingNote";
import useDashboard from "../services/useDashboard";
import useMasters from "../services/useMasters";
import useProfile from "../services/useProfile";
import SkeletonLoader from "../components/SkeletonLoader";

const SwalAlert = withReactContent(Swal);

export default function Dashboard() {
  const navigate = useNavigate();
  const { updateDistributor } = useMasters();
  const {
    postFileUploadProfile,
    sendOTP,
    verifyOTPSaveDuplicate,
    getPhoneEmailDuplicateStatus,
    updatePhoneEmailDuplicate,
  } = useProfile(localStorage.getItem("dist_id"));
  const {
    error,
    getActivityDetails,
    getDistributorDetails,
    checkForTDSPopup,
    acceptTDSPopup,
    checkIDTransferStatus,
  } = useDashboard(localStorage.getItem("dist_id"));
  const [activityDetails, setActivityDetails] = useState({});
  const [distributorDetails, setDistributorDetails] = useState({});
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [popupQueue, setPopupQueue] = useState([]);
  const [isPopupActive, setIsPopupActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [duplicateStatus, setDuplicateStatus] = useState(null);
  const [duplicateResolutionInProgress, setDuplicateResolutionInProgress] = useState(false);
  const [apiLoadStatus, setApiLoadStatus] = useState({
    activityDetails: false,
    distributorDetails: false,
    tdsPopup: false,
    idTransfer: false,
    duplicateCheck: false,
  });

  useEffect(() => {
    if (error) {
      WishToaster({ toastMessage: error });
    }
  }, [error]);

  // Check if all critical APIs have loaded
  const checkAllAPIsLoaded = () => {
    const allLoaded = Object.values(apiLoadStatus).every((status) => status === true);
    if (allLoaded) {
      setIsLoading(false);
    }
  };

  // Update loading status when API load status changes
  useEffect(() => {
    checkAllAPIsLoaded();
  }, [apiLoadStatus]);

  // Popup Queue Management
  const addToPopupQueue = (popup) => {
    setPopupQueue((prev) => [...prev, popup]);
  };

  const processNextPopup = () => {
    if (popupQueue.length > 0 && !isPopupActive) {
      // Sort by priority (lower number = higher priority)
      const sortedQueue = [...popupQueue].sort((a, b) => a.priority - b.priority);
      const nextPopup = sortedQueue[0];
      setPopupQueue((prev) => prev.filter((popup) => popup !== nextPopup));
      setIsPopupActive(true);
      return nextPopup;
    }
    return null;
  };

  const finishCurrentPopup = () => {
    setIsPopupActive(false);
    // Process next popup after a short delay
    setTimeout(() => {
      processNextPopup();
    }, 500);
  };

  // Function to check if any documents are missing
  const checkDocumentFlags = (distributorData) => {
    if (distributorData?.dist_document_flag) {
      const flags = distributorData.dist_document_flag;
      const missingDocuments = Object.keys(flags).some((key) => key !== "skip_flag" && flags[key] === 0);

      if (missingDocuments) {
        addToPopupQueue({
          type: "DOCUMENT_VERIFICATION",
          priority: 2, // Second priority
          data: distributorData.dist_document_flag,
        });
      }
    }
  };

  // Document type mappings based on KYDS system
  const documentTypeMappings = {
    bank_flag: { document_type_id: 11, document_type_code: "DTBP" },
    aadhar_flag: { document_type_id: 1, document_type_code: "DTAAD" },
    pan_flag: { document_type_id: 2, document_type_code: "DTPAN" },
    address_flag: { document_type_id: 3, document_type_code: "DTAP" },
  };

  // Function to handle document upload
  const handleDocumentUpload = (documentType, file) => {
    if (!file) return;

    const mapping = documentTypeMappings[documentType];
    if (!mapping) {
      console.error(`Unknown document type: ${documentType}`);
      return;
    }

    const formData = new FormData();
    formData.append("distributor_id", distributorDetails.distributor_id);
    formData.append("document_type_id", mapping.document_type_id);
    formData.append("document_type_code", mapping.document_type_code);
    formData.append("document", file);

    postFileUploadProfile(
      formData,
      (data) => {
        console.log("File uploaded successfully:", data);
        // Refresh distributor details to update document flags
        getDistributorDetails((updatedData) => {
          setDistributorDetails(updatedData);
          updateDistributor(updatedData);
        });
      },
      (error) => {
        console.error("File upload failed:", error);
      }
    );
  };

  // Function to handle skip action
  const handleSkipDocuments = () => {
    setShowDocumentModal(false);
    finishCurrentPopup();
  };

  const executeFastTrackSuccessPopup = (data) => {
    SwalAlert.fire({
      icon: "success",
      title: "🚀 Fast Track VBO Achieved!",
      html: `
        <p>Congratulations <strong>${data.name}</strong>! 🎉</p>

        <p>You've successfully completed <strong>100 PV</strong> within the first 30 minutes of enrollment.</p>

        <p>You now have <strong>early access</strong> to:</p>
        <ul style="text-align: left;">
          <li>📝 Enrollment</li>
          <li>🧬 Genealogy</li>
        </ul>
      `,
      confirmButtonText: "Awesome!",
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.setItem("fast_track_congrats_shown", "1");
      }
      finishCurrentPopup();
    });
  };

  const executeFastTrackReminderPopup = (data) => {
    const remaining = Math.floor(30 - data.timeDiffInMinutes);
    SwalAlert.fire({
      icon: "info",
      title: "⏳ Hurry Up!",
      html: `
        <p>Hey <strong>${
          data.name
        }</strong>, you still have <strong>${remaining} minutes</strong> left to complete a 100 PV order and unlock early access to:</p>
        <ul style="text-align: left;">
          <li>📝 Enrollment</li>
          <li>🧬 Genealogy</li>
        </ul>
        <p>Act fast and grab your early privileges!</p>
    
        <div style="display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; margin-top: 10px;">
          <a id="placeOrderButton" href="${
            data.store_link || "/store"
          }" target="_blank" style="background-color: #007bff; color: white; padding: 8px 12px; border-radius: 5px; text-decoration: none; display: flex; align-items: center;">
            🛒 Place Order
          </a>
          <button id="copyButton" style="background-color: #28a745; color: white; padding: 8px 12px; border-radius: 5px; border: none; display: flex; align-items: center; cursor: pointer;">
            📋 Copy Link
          </button>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: "Later",
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        const copyBtn = document.getElementById("copyButton");
        if (copyBtn) {
          copyBtn.addEventListener("click", () => {
            navigator.clipboard.writeText(data.store_link || "/store");
            const originalText = copyBtn.innerHTML;
            copyBtn.innerHTML = "✅ Copied!";
            copyBtn.style.backgroundColor = "#20c997";
            setTimeout(() => {
              copyBtn.innerHTML = originalText;
              copyBtn.style.backgroundColor = "#28a745";
            }, 5000);
          });
        }
      },
    }).then(() => {
      finishCurrentPopup();
    });
  };

  const executeTDSWarningPopup = (data) => {
    SwalAlert.fire({
      icon: "warning",
      width: "50rem",
      title: "🚨Attention!🚨",
      html: data.tds_message,
      allowOutsideClick: false,
      confirmButtonText: "I acknowledge",
      allowEnterKey: false,
      allowEscapeKey: false,
    }).then(() => {
      acceptTDSPopup(
        {
          distributor_id: localStorage.getItem("dist_id"),
          accept_message: 1,
        },
        (data) => {
          navigate("/myprofile", { state: { selectedTab: 3 } });
        },
        (error) => {}
      );
      finishCurrentPopup();
    });
  };

  const executeIDTransferPopup = (data) => {
    SwalAlert.fire({
      icon: "warning",
      width: "50rem",
      title: "🚨Attention!🚨",
      html: `
        <p>We have noticed that there has been some changes in your KYDS details.</p>
        <p><strong>Please submit your documents for re-verification.</strong></p>
        <p>For any clarification, please contact DCS at <strong>18001034916 (toll-free)</strong> or <strong>080-43532020</strong>.</p>
        <p>Thank you for your cooperation!</p>
      `,
      allowOutsideClick: false,
      confirmButtonText: "I acknowledge",
      allowEnterKey: false,
      allowEscapeKey: false,
    }).then(() => {
      navigate("/myprofile", { state: { selectedTab: 3 } });
      finishCurrentPopup();
    });
  };

  const needsDuplicateResolution = (duplicateData) =>
    Boolean(duplicateData?.is_phone_duplicate) || Boolean(duplicateData?.is_email_duplicate);

  const executeDuplicateContactPopup = (data) => {
    if (duplicateResolutionInProgress) {
      return;
    }
    setDuplicateResolutionInProgress(true);

    const distId = Number(localStorage.getItem("dist_id"));
    const hardEnforcement = !data?.is_skippable;

    const openDuplicateModal = (latestData) => {
      const hasPhoneDuplicate = Boolean(latestData?.is_phone_duplicate);
      const hasEmailDuplicate = Boolean(latestData?.is_email_duplicate);
      const stepOrder = hasPhoneDuplicate ? "phone" : hasEmailDuplicate ? "email" : null;

      if (!stepOrder) {
        setDuplicateResolutionInProgress(false);
        finishCurrentPopup();
        return;
      }

      let remarksValue = "";
      let targetValue =
        latestData?.[stepOrder] != null && latestData?.[stepOrder] !== ""
          ? String(latestData[stepOrder])
          : "";
      let otpValue = "";
      let isSendingOtp = false;
      let isVerifyingOtp = false;
      let isCommitting = false;

      const isPhoneStep = stepOrder === "phone";
      const title = isPhoneStep ? "Phone number duplicate detected" : "Email duplicate detected";
      const fieldLabel = isPhoneStep ? "Phone number" : "Email";
      const duplicateCount = isPhoneStep
        ? latestData?.phone_duplicate_details?.length ?? 0
        : latestData?.email_duplicate_details?.length ?? 0;

      const validateField = (value) => {
        const trimmed = (value ?? "").toString().trim();
        if (isPhoneStep) {
          return /^\d{10,15}$/.test(trimmed);
        }
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
      };

      const readDuplicateFormFromDom = () => {
        const popup = Swal.getPopup();
        if (!popup) {
          return { value: "", otp: "", remarks: "" };
        }
        return {
          value: (popup.querySelector("#duplicate-value-input")?.value ?? "").toString().trim(),
          otp: (popup.querySelector("#duplicate-otp-input")?.value ?? "").toString().trim(),
          remarks: (popup.querySelector("#duplicate-remarks-input")?.value ?? "").toString().trim(),
        };
      };

      const sendOtpHandler = () => {
        const { value: trimmed } = readDuplicateFormFromDom();
        if (!validateField(trimmed)) {
          Swal.showValidationMessage(`Please enter a valid ${fieldLabel.toLowerCase()} before sending OTP.`);
          return;
        }
        targetValue = trimmed;
        isSendingOtp = true;
        Swal.resetValidationMessage();
        Swal.update({
          html: buildHtml(),
        });
        sendOTP(
          isPhoneStep,
          isPhoneStep ? { phone: trimmed } : { email: trimmed },
          () => {
            isSendingOtp = false;
            Swal.resetValidationMessage();
            Swal.update({
              html: buildHtml(),
            });
          },
          (error) => {
            isSendingOtp = false;
            Swal.showValidationMessage(error || "Failed to send OTP. Please try again.");
            Swal.update({
              html: buildHtml(),
            });
          }
        );
      };

      const verifyAndCommitHandler = () => {
        const { value: trimmed, otp: otpFromDom, remarks: trimmedRemarks } = readDuplicateFormFromDom();

        if (!validateField(trimmed)) {
          Swal.showValidationMessage(`Please enter a valid ${fieldLabel.toLowerCase()}.`);
          return;
        }
        if (otpFromDom.length < 4) {
          Swal.showValidationMessage("Please enter a valid OTP.");
          return;
        }
        if (trimmedRemarks.length === 0) {
          Swal.showValidationMessage("Remarks is mandatory.");
          return;
        }

        isVerifyingOtp = true;
        Swal.resetValidationMessage();
        Swal.update({
          html: buildHtml(),
        });

        verifyOTPSaveDuplicate(
          isPhoneStep,
          isPhoneStep
            ? { phone: trimmed, phone_otp: otpFromDom }
            : { email: trimmed, email_otp: otpFromDom },
          () => {
            isVerifyingOtp = false;
            isCommitting = true;
            Swal.update({
              html: buildHtml(),
            });
            updatePhoneEmailDuplicate(
              {
                distributor_id: distId,
                phone: isPhoneStep ? trimmed : "",
                email: isPhoneStep ? "" : trimmed,
                phone_flag: isPhoneStep ? 1 : 0,
                email_flag: isPhoneStep ? 0 : 1,
                remarks: trimmedRemarks,
              },
              () => {
                isCommitting = false;
                getPhoneEmailDuplicateStatus(
                  { distributor_id: distId },
                  (newData) => {
                    setDuplicateStatus(newData);
                    if (needsDuplicateResolution(newData)) {
                      openDuplicateModal(newData);
                    } else {
                      localStorage.removeItem("duplicate_resolution_pending");
                      Swal.close();
                      setDuplicateResolutionInProgress(false);
                      finishCurrentPopup();
                    }
                  },
                  () => {
                    Swal.showValidationMessage("Saved, but failed to refresh status. Please reopen dashboard.");
                  }
                );
              },
              (error) => {
                isCommitting = false;
                Swal.showValidationMessage(error || "Failed to complete update. Please try again.");
                Swal.update({
                  html: buildHtml(),
                });
              }
            );
          },
          (error) => {
            isVerifyingOtp = false;
            Swal.showValidationMessage(error || "OTP verification failed. Please try again.");
            Swal.update({
              html: buildHtml(),
            });
          }
        );
      };

      // Swal.update() replaces inner HTML and removes direct button listeners. Delegate from the
      // popup element so Send OTP / Verify keep working after every re-render.
      const attachDuplicateContactDelegation = (popup) => {
        if (!popup || popup.dataset.duplicateContactDelegation === "1") return;
        popup.dataset.duplicateContactDelegation = "1";
        popup.addEventListener("click", (e) => {
          const t = e.target;
          if (t?.id === "duplicate-send-otp-btn" || t?.closest?.("#duplicate-send-otp-btn")) {
            e.preventDefault();
            sendOtpHandler();
          }
          if (t?.id === "duplicate-submit-btn" || t?.closest?.("#duplicate-submit-btn")) {
            e.preventDefault();
            verifyAndCommitHandler();
          }
        });
        popup.addEventListener("input", (e) => {
          const id = e.target?.id;
          if (id === "duplicate-value-input") targetValue = e.target.value;
          if (id === "duplicate-otp-input") otpValue = e.target.value;
          if (id === "duplicate-remarks-input") remarksValue = e.target.value;
        });
      };

      const buildHtml = () => `
        <div style="text-align:left;">
          <p>
            Your ${fieldLabel.toLowerCase()} is currently used in multiple accounts (${duplicateCount} other account(s)).
            To continue, complete verification/update now.
          </p>
          <div class="mb-1">
            <label for="duplicate-value-input" style="font-weight:600;">${fieldLabel}</label>
            <input
              id="duplicate-value-input"
              class="swal2-input"
              style="margin:6px 0 0 0;width:100%;"
              placeholder="Enter ${fieldLabel.toLowerCase()}"
              value="${(targetValue ?? "").toString().replace(/"/g, "&quot;")}"
            />
            <small class="text-muted" style="display:block;margin-top:6px;">
              Pre-filled from your profile when available. Clear the field and enter a new ${fieldLabel.toLowerCase()} if you want to change it before sending OTP.
            </small>
          </div>

          <div class="mb-1">
            <button type="button" id="duplicate-send-otp-btn" class="swal2-confirm swal2-styled" style="margin:0;background:#3085d6;" ${
              isSendingOtp || isVerifyingOtp || isCommitting ? "disabled" : ""
            }>
              ${isSendingOtp ? "Sending OTP..." : "Send OTP"}
            </button>
          </div>

          <div class="mb-1">
            <label for="duplicate-otp-input" style="font-weight:600;">OTP</label>
            <input
              id="duplicate-otp-input"
              class="swal2-input"
              style="margin:6px 0 0 0;width:100%;"
              placeholder="Enter OTP"
              value="${(otpValue ?? "").toString()}"
            />
          </div>

          <div class="mb-1">
            <label for="duplicate-remarks-input" style="font-weight:600;">Remarks</label>
            <textarea
              id="duplicate-remarks-input"
              class="swal2-textarea"
              style="margin:6px 0 0 0;width:100%;"
              placeholder="Enter remarks"
            >${(remarksValue ?? "").toString()}</textarea>
          </div>

          <div class="mb-0">
            <button type="button" id="duplicate-submit-btn" class="swal2-confirm swal2-styled" style="margin:0;background:#28a745;" ${
              isSendingOtp || isVerifyingOtp || isCommitting ? "disabled" : ""
            }>
              ${isVerifyingOtp ? "Verifying OTP..." : isCommitting ? "Finalizing..." : "Verify & Continue"}
            </button>
          </div>
        </div>
      `;

      SwalAlert.fire({
        icon: "warning",
        title,
        html: buildHtml(),
        showConfirmButton: false,
        showCancelButton: !hardEnforcement,
        cancelButtonText: "Later",
        allowOutsideClick: !hardEnforcement,
        allowEscapeKey: !hardEnforcement,
        didOpen: (popup) => {
          attachDuplicateContactDelegation(popup);
        },
      }).then((result) => {
        if (result.dismiss && !hardEnforcement) {
          setDuplicateResolutionInProgress(false);
          finishCurrentPopup();
        }
      });
    };

    openDuplicateModal(data);
  };

  useEffect(() => {
    let payload = { distributor_id: localStorage.getItem("dist_id") };

    getActivityDetails((data) => {
      setActivityDetails(data);
      setApiLoadStatus((prev) => ({ ...prev, activityDetails: true }));
    });

    getDistributorDetails((data) => {
      setDistributorDetails(data);
      updateDistributor(data);

      // Check for missing documents first
      checkDocumentFlags(data);

      // Check fast track condition
      const joinTime = new Date(data.join_time);
      const currentTime = new Date();
      const timeDiffInMinutes = (currentTime - joinTime) / 60000;

      if (
        data.fast_track_vbo === 1 &&
        !localStorage.getItem("fast_track_congrats_shown") &&
        data?.mprofession_id !== 7 &&
        data?.distributor_rank?.rank_id === 1
      ) {
        // User achieved 100PV within 30 minutes
        addToPopupQueue({
          type: "FAST_TRACK_SUCCESS",
          priority: 5,
          data: data,
        });
      } else if (data.fast_track_vbo === 0 && timeDiffInMinutes < 30 && data?.mprofession_id !== 7) {
        addToPopupQueue({
          type: "FAST_TRACK_REMINDER",
          priority: 4,
          data: { ...data, timeDiffInMinutes },
        });
      }

      setApiLoadStatus((prev) => ({ ...prev, distributorDetails: true }));
    });

    checkForTDSPopup(
      payload,
      (data) => {
        if (data.tds_message) {
          addToPopupQueue({
            type: "TDS_WARNING",
            priority: 1, // Highest priority
            data: {
              tds_message: `
                <p>Due to the non-linking of your PAN and Aadhaar, the company has received a tax notice regarding a shortfall in tax deduction on your commissions.</p>
                <p><strong>To avoid the following:</strong></p>
                <ul style="text-align: left;">
                  <li>20% tax deduction on future commissions.</li>
                  <li>Recovery of differential tax from your commissions.</li>
                </ul>
                <p>Please link your PAN and Aadhaar by <strong>10th February 2025</strong> and update your KYC details in your virtual office. Notify us once done.</p>
                <p><strong>Useful Links:</strong></p>
                <ul style="text-align: left;">
                  <li><a href="https://eportal.incometax.gov.in/iec/foservices/#/pre-login/link-aadhaar-status" target="_blank">Check PAN-Aadhaar Link Status</a></li>
                  <li><a href="https://eportal.incometax.gov.in/iec/foservices/#/pre-login/bl-link-aadhaar" target="_blank">Link PAN & Aadhaar</a></li>
                </ul>
                <p>For any clarification, please contact DCS at <strong>18001034916 (toll-free)</strong> or <strong>080-43532020</strong>.</p>
                <p>Your cooperation is appreciated.</p>
              `,
            },
          });
        } else {
          checkIDTransferStatus(
            payload,
            (data) => {
              if (data) {
                addToPopupQueue({
                  type: "ID_TRANSFER",
                  priority: 3,
                  data: data,
                });
              }
              setApiLoadStatus((prev) => ({ ...prev, idTransfer: true }));
            },
            (error) => {
              setApiLoadStatus((prev) => ({ ...prev, idTransfer: true }));
            }
          );
        }
        setApiLoadStatus((prev) => ({ ...prev, tdsPopup: true }));
      },
      (error) => {
        setApiLoadStatus((prev) => ({ ...prev, tdsPopup: true }));
      }
    );

    getPhoneEmailDuplicateStatus(
      { distributor_id: payload.distributor_id },
      (data) => {
        setDuplicateStatus(data);
        if (needsDuplicateResolution(data)) {
          if (!data?.is_skippable) {
            localStorage.setItem("duplicate_resolution_pending", "1");
          }
          addToPopupQueue({
            type: "PHONE_EMAIL_DUPLICATE",
            priority: 0, // Highest priority in dashboard queue
            data,
          });
        } else {
          localStorage.removeItem("duplicate_resolution_pending");
        }
        setApiLoadStatus((prev) => ({ ...prev, duplicateCheck: true }));
      },
      () => {
        setApiLoadStatus((prev) => ({ ...prev, duplicateCheck: true }));
      }
    );
  }, []);

  const distSkeleton = () => {
    return (
      <>
        <div className="skeleton-image"></div>
        <div className="skeleton-text">
          <div className="skeleton-line"></div>
          <div className="skeleton-line"></div>
          <div className="skeleton-line"></div>
        </div>
        <div className="col-sm-12">
          <div className="card is-loading">
            <div className="row">
              <div className="col-sm-12 m-0 p-0">
                <div className="content-1">
                  <p></p>
                  <p></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const carouselData = [
    {
      id: 1,
      imageSrc: "../assets/app-assets/images/carousel/ipulse.jpg",
      altText: "First slide",
    },
    {
      id: 2,
      imageSrc: "../assets/app-assets/images/carousel/ICOFFEE.jpg",
      altText: "Second slide",
    },
    {
      id: 3,
      imageSrc: "../assets/app-assets/images/carousel/ICARE.jpg",
      altText: "Third slide",
    },
    {
      id: 4,
      imageSrc: "../assets/app-assets/images/carousel/iglow.jpg",
      altText: "Fourth slide",
    },
    {
      id: 5,
      imageSrc: "../assets/app-assets/images/carousel/ISLIM.jpg",
      altText: "Fifth slide",
    },
  ];

  const RenderPage = function () {
    return (
      <PageLayout activeSideMenu="1" pageTitle="Dashboard">
        <div className="row">
          <div className="col-sm-12">
            <WishSimpleCard cardBodyClassName="pb-0">
              <div className="row">
                <div className="col-12">
                  <h3>Welcome back {distributorDetails?.name}!</h3>
                </div>
                <Swiper
                  slidesPerView={1}
                  modules={[Autoplay, Pagination, Navigation]}
                  grabCursor={true}
                  pagination={{
                    clickable: false,
                    dynamicBullets: true,
                  }}
                  autoplay={{
                    delay: 4000,
                    disableOnInteraction: false,
                  }}
                >
                  {(carouselData ?? []).map((item, index) => {
                    return (
                      <SwiperSlide key={index}>
                        <img className="rounded-lg w-100 h-100" src={item.imageSrc} alt={item.altText} />
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </div>
            </WishSimpleCard>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-6">
            <WishSimpleCard className={"equal-height"}>
              <h5 className="text-center">My Info</h5>
              {Object.keys(distributorDetails).length > 0 ? (
                <div className="text-center mt-2">
                  <img
                    src={`../../assets/app-assets/images/badges/${distributorDetails?.distributor_rank?.rank_title}.png`}
                    alt=""
                    style={{ width: "50px" }}
                  />
                  <EmptyBox height="10px" />
                  <p style={{ lineHeight: "1.0" }}>
                    <label className="d-block">{distributorDetails?.distributor_id}</label>
                    <h5>{distributorDetails?.name}</h5>
                    <label>{distributorDetails?.distributor_rank?.rank_title}</label>
                  </p>
                  <div className="row">
                    <div className="col-4">
                      <label>Paid as rank</label>
                    </div>
                    <div className="col-8">
                      <h6 className="m-0 text-right">{activityDetails.current_paid_rank_title} </h6>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-4">
                      <label>Profile Completion</label>
                    </div>
                    <div className="col-8">
                      <Link to={"/myprofile"}>
                        <div className="progress progress-analysis" role="progressbar" aria-label="Progress bar">
                          <div
                            className="progress-bar progress-bar-analysis"
                            style={{
                              width: `${
                                distributorDetails?.profile_completion_percent
                                  ? distributorDetails?.profile_completion_percent
                                  : 0
                              }%`,
                            }}
                          >
                            <strong>
                              {distributorDetails?.profile_completion_percent
                                ? distributorDetails?.profile_completion_percent
                                : 0}{" "}
                              %
                            </strong>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                distSkeleton()
              )}
            </WishSimpleCard>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-6">
            <WishSimpleCard className={"equal-height"}>
              <h5 className="text-center">Activity Status</h5>
              {Object.keys(activityDetails).length > 0 ? (
                <>
                  <WishFlexBox className="rounded-lg border-light bg-light-1 mt-2">
                    <div className="col-12 text-center">
                      <label className="fs-2 d-block">{activityDetails.current_pv_active}</label>
                      <label>Activation PV</label>
                    </div>
                  </WishFlexBox>
                  <div className="row mt-3">
                    <div className="col-6">
                      <label className="text-info">Next activation week: </label>
                    </div>
                    <div className="col-6">
                      <h5 className="text-right">{activityDetails.next_activation_week}</h5>
                    </div>
                  </div>
                  <label className="font-weight-bold text-danger col-12 text-center">{activityDetails.comment}</label>
                </>
              ) : (
                <SkeletonLoader rows={4} />
              )}
            </WishSimpleCard>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-6">
            <WishSimpleCard className={"equal-height"}>
              <div className="col-12">
                <h5 className="text-center">Need support?</h5>
                <p className="text-center mb-2">Contact your sponsor!</p>
              </div>
              {Object.keys(distributorDetails).length > 0 ? (
                <div className="col-12 text-center">
                  <img
                    src={`../../assets/app-assets/images/badges/${distributorDetails?.sponsor_profile?.sponsor_rank?.rank_title}.png`}
                    alt=""
                    style={{ width: "50px", marginBottom: "5px" }}
                  />
                  <h6>{distributorDetails?.sponsor_profile?.sponsor_name}</h6>
                  <p style={{ lineHeight: "1.0" }}>
                    <label className="d-block">{distributorDetails?.sponsor_profile?.sponsor_distributor_id}</label>
                    <small>{distributorDetails?.sponsor_profile?.sponsor_rank?.rank_title}</small>
                  </p>
                  <img
                    src="../../assets/app-assets/images/elements/telephone-call.png"
                    alt=""
                    style={{
                      width: "40px",
                      marginRight: "15px",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      navigator.clipboard.writeText(distributorDetails?.sponsor_profile?.sponsor_phone);
                      SwalAlert.fire({
                        icon: "success",
                        title: "Contact copied",
                        text: `Sponser mobile no ${distributorDetails?.sponsor_profile?.sponsor_phone} copied to clipboard.`,
                      });
                    }}
                  />
                  <img
                    src="../../assets/app-assets/images/elements/mail.png"
                    alt=""
                    style={{ width: "40px", cursor: "pointer" }}
                    onClick={(e) => {
                      navigator.clipboard.writeText(distributorDetails?.sponsor_profile?.sponsor_email);
                      SwalAlert.fire({
                        icon: "success",
                        title: "E-mail copied",
                        text: `Sponser e-mail ${distributorDetails?.sponsor_profile?.sponsor_email} copied to clipboard.`,
                      });
                    }}
                  />
                  <div className="mt-2 mb-1">
                    <h6 className="mb-1">Great News!!! The Indusviva mobile app is here!</h6>
                    <a
                      className="btn-downlaod btn-google custom-heartBeat"
                      href="https://play.google.com/store/apps/details?id=com.indusviva.app.indusviva&listing=poster"
                      target="_blank"
                      rel="noreferrer"
                      title="Google Play"
                    >
                      Google Play
                    </a>
                    <a
                      className="btn-downlaod btn-apple custom-heartBeat"
                      href="https://apps.apple.com/app/indusviva-app/id6474152992?ppid=c85689e0-40d5-4298-bdb3-c572f3a25e0b"
                      target="_blank"
                      rel="noreferrer"
                      title="App Store"
                    >
                      App Store
                    </a>
                  </div>
                </div>
              ) : (
                distSkeleton()
              )}
            </WishSimpleCard>
          </div>
        </div>
      </PageLayout>
    );
  };

  // Popup Queue Processor
  useEffect(() => {
    const nextPopup = processNextPopup();
    if (nextPopup) {
      switch (nextPopup.type) {
        case "DOCUMENT_VERIFICATION":
          setShowDocumentModal(true);
          break;
        case "FAST_TRACK_SUCCESS":
          executeFastTrackSuccessPopup(nextPopup.data);
          break;
        case "FAST_TRACK_REMINDER":
          executeFastTrackReminderPopup(nextPopup.data);
          break;
        case "TDS_WARNING":
          executeTDSWarningPopup(nextPopup.data);
          break;
        case "PHONE_EMAIL_DUPLICATE":
          executeDuplicateContactPopup(nextPopup.data);
          break;
        case "ID_TRANSFER":
          executeIDTransferPopup(nextPopup.data);
          break;
        default:
          finishCurrentPopup();
      }
    }
  }, [popupQueue, isPopupActive]);

  // Show document verification modal when needed
  useEffect(() => {
    if (showDocumentModal && distributorDetails?.dist_document_flag) {
      SwalAlert.fire({
        html: (
          <DocumentVerificationModal
            documentFlags={distributorDetails.dist_document_flag}
            onUpload={handleDocumentUpload}
            onSkip={handleSkipDocuments}
          />
        ),
        showConfirmButton: false,
        showCancelButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        width: "90%",
        // maxWidth: "800px"
      });
    }
  }, [showDocumentModal, distributorDetails?.dist_document_flag]);

  // Full window loading spinner
  if (isLoading) {
    return <LoadingNote />;
  }

  return <RenderPage />;
}
